import { CardSubWrapper, CardWrapper } from 'admin/components/wrappers';
import { GridWrapper } from 'admin/pages/SponsersPage/SponsersTable';
import BackNoCell from 'app/components/CustomDataGrid/Cells/BackNoCell';
import RiderNameCell from 'app/components/CustomDataGrid/Cells/RiderName';
import StatusChip from 'app/components/StatusChip';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import React from 'react';
import { BackNumbersWrapper, CurrentSeason } from 'services/openapi';
import styled from 'styled-components';
import { CustomDeletePopup } from 'app/components/CustomDeletePopup';

type Props = {
    setPageSkip: React.Dispatch<React.SetStateAction<number | undefined>>;
    pageSkip: number | undefined;
    fetchAdminBacknumberList: (
        page?: number,
        limit?: number,
        searchInput?: string,
    ) => void;
    debounceRiderSearch: string;
    backnumberList: BackNumbersWrapper | undefined;
    makeBacknumberAvailable: (
        backNumber?: string,
        OnSuccess?: () => void,
    ) => void;
    pageSize: number;
};

const BackNumberTable = ({
    setPageSkip,
    pageSkip,
    fetchAdminBacknumberList,
    debounceRiderSearch,
    backnumberList,
    makeBacknumberAvailable,
    pageSize,
}: Props) => {
    const [showPopup, setShowPopup] = React.useState<boolean>(false);
    const [backnumber, setBacknumber] = React.useState<string>('');
    const handleConfirmClick = () => {
        // Calculate next page before making back number available
        const currentItemCount = backnumberList?.backNumbers?.length ?? 0;
        let nextPageSkip = pageSkip;

        // If this is the last item on current page and not on first page, go to previous page
        if (currentItemCount <= 1 && pageSkip && pageSkip > 0) {
            nextPageSkip = pageSkip - pageSize;
            setPageSkip(nextPageSkip);
        }

        makeBacknumberAvailable(backnumber, () => {
            setShowPopup(false);
            fetchAdminBacknumberList(
                nextPageSkip ?? 0,
                pageSize,
                debounceRiderSearch,
            );
        });
    };
    const handlePageChange = event => {
        setPageSkip(event.page.skip);
        fetchAdminBacknumberList(event.page.skip, pageSize, debounceRiderSearch);
    };
    const BackNumberColumns = [
        {
            field: 'name',
            title: 'Back Number',
            cell: e => <BackNoCell e={e} title={e.dataItem.name} />,
            width: '150',
            sortable: true,
        },
        {
            field: 'isActive',
            title: 'Status',
            cell: e => (
                <td>
                    <StatusChip
                        text={e?.dataItem?.rider !== null ? 'Assigned' : 'Available'}
                        StatusType={e?.dataItem?.rider !== null ? 'inactive' : 'active'}
                    />
                </td>
            ),
            width: '150',
            sortable: false,
        },
        {
            field: 'riderName',
            title: 'Rider Name',
            cell: e => (
                <RiderNameCell
                    e={e}
                    isHideView
                    title={
                        e?.dataItem?.rider?.firstName
                            ? e.dataItem?.rider?.firstName + ' ' + e.dataItem?.rider?.lastName
                            : '-'
                    }
                    profileClassName={e?.dataItem.rider ? '' : 'hide-image-style'}
                />
            ),
            width: '300',
            sortable: true,
            className: 'rider-name-style',
            headerClassName: 'rider-header-style',
        },
        {
            field: 'action',
            title: 'Action',
            cell: e => (
                <td>
                    <div style={{ padding: '8px' }}>
                        {e?.dataItem?.rider !== null ? (
                            <button
                                style={{
                                    backgroundColor: '#FF0000',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    setShowPopup(true);
                                    setBacknumber(e.dataItem.name);
                                }}
                            >
                                Make Available
                            </button>
                        ) : (
                            '-'
                        )}
                    </div>
                </td>
            ),
            width: '120',
            sortable: false,
        },
    ];
    return (
        <CardWrapper style={{ paddingTop: '15px' }}>
            <CardSubWrapper className="d-flex"></CardSubWrapper>
            <StyledGridWrapper>
                <CustomDataGrid
                    showPagination
                    data={backnumberList?.backNumbers ?? []}
                    columns={BackNumberColumns}
                    pageSize={pageSize}
                    totalCount={backnumberList?.pageDetails?.count || 0}
                    onPageChange={event => {
                        handlePageChange(event);
                    }}
                    skip={pageSkip}
                    sortable={{
                        allowUnsort: false,
                        mode: 'single',
                    }}
                />
                <CustomDeletePopup
                    headerUnderline={false}
                    popupLabel="Back Number Availability"
                    setVisible={setShowPopup}
                    visible={showPopup}
                    align="centre"
                    btnLabel1="Cancel"
                    btnLabel2="Confirm"
                    handlebtn1Click={() => {
                        setShowPopup(false);
                    }}
                    handlebtn2Click={handleConfirmClick}
                >
                    <div>Are you sure you want to make this back number available?</div>
                </CustomDeletePopup>
            </StyledGridWrapper>
        </CardWrapper>
    );
};

export default BackNumberTable;
const StyledGridWrapper = styled(GridWrapper)`
  tr {
    height: 55px !important;
  }
  .hide-image-style {
    img {
      display: none;
    }
  }
`;
